import { useStore } from '../store/store';
import { useRemoteConfig } from 'core/hooks/use-remote-config';
import { backupFlow } from '../consts';
import { transitionPages } from '../consts';
import { REDIRECT_RULES } from '../constants/redirectRules';

const PAGES_FOR_WOMEN = ['last-period', 'your-biggest-concern', 'feel-like'];
const PAGES_FOR_GPL = ['currently-taking', 'semaglutide-dose', 'side-effects', 'noticed-muscle'];
const PAGES_FOR_FASTING = ['fasting-details'];
const PAGES_FOR_MEALPLAN = ['offplan-behavior'];

export function useNav() {
  // @ts-ignore
  const { 
    routeGroups: flow, 
    transitionVisibilityGender,
    dynamicRedirectRules
  } = useRemoteConfig();
  
  const flatFlow = flow?.length > 0 ? flow : backupFlow;
  const quizFlow = flatFlow.flat();
  const pageConfig = [...quizFlow];

  const setPage = useStore((state) => state.setPage);
  const currentPage = useStore((state) => state.currentPage);
  const addVisitedPage = useStore((state) => state.addVisitedPage);
  const currentIndex = pageConfig.indexOf(currentPage);

  const getAllRedirectRules = () => {
    const staticRules = REDIRECT_RULES || [];
    const dynamicRules = dynamicRedirectRules || [];
    return { staticRules, dynamicRules };
  };

  const getNextPageWithRules = () => {
    const currentState = useStore.getState();
    const currentPageValue = currentState.currentPage;
    const pageAnswers = currentState.pageAnswers;
    const usedGlpValue = currentState.usedGlp;
    const currentIndexValue = pageConfig.indexOf(currentPageValue);

    const { staticRules, dynamicRules } = getAllRedirectRules();

    const applicableStaticRule = staticRules.find((rule) => 
      rule.from === currentPageValue && 
      currentState[rule.field] === rule.value
    );

    if (applicableStaticRule) {
      const targetPage = applicableStaticRule.to;
      const targetAllowed = pageConfig.includes(targetPage) && isPageAllowed(targetPage, usedGlpValue);
      
      if (targetAllowed) {
        return targetPage;
      }
    }

    //@ts-ignore
    const rulesForCurrentPage = dynamicRules.filter(rule => rule.from === currentPageValue);

    for (const rule of rulesForCurrentPage) {
      //@ts-ignore
      const pageAnswer = pageAnswers[rule.field];
      //@ts-ignore
      const conditionMet = pageAnswer === rule.value;
      
      if (conditionMet) {
        //@ts-ignore
        const targetPage = rule.to;
        const targetAllowed = pageConfig.includes(targetPage) && isPageAllowed(targetPage, usedGlpValue);
        
        if (targetAllowed) {
          return targetPage;
        }
      }
    }

    for (let i = currentIndexValue + 1; i < pageConfig.length; i++) {
      const page = pageConfig[i];
      const allowed = isPageAllowed(page, usedGlpValue);

      if (allowed) {
        return page;
      }
    }

    return null;
  };

  const isPageVisible = (page: string, currentUsedGlp?: string) => {
    const currentState = useStore.getState();
    const usedGlpValue = currentUsedGlp !== undefined ? currentUsedGlp : currentState.usedGlp;
    const gender = currentState.gender;

    if (PAGES_FOR_WOMEN.includes(page)) {
      return gender === 'Female';
    }

    if (PAGES_FOR_GPL.includes(page)) {
      const shouldHide = usedGlpValue === 'Never used' || usedGlpValue === undefined;
      return !shouldHide;
    }

    if (PAGES_FOR_FASTING.includes(page)) {
      const fastingExpValue = currentState.fastingExperience;
      return fastingExpValue !== 'neverTried';
    }

    if (PAGES_FOR_MEALPLAN.includes(page)) {
      const mealplanExpValue = currentState.mealplanExperience;
      return mealplanExpValue !== 'Never used any';
    }

    if (transitionPages.includes(page)) {
      if (transitionVisibilityGender === 'male-only' && gender === 'Male') return true;
      if (transitionVisibilityGender === 'female-only' && gender === 'Female') return true;
      if (transitionVisibilityGender === 'all') return true;
      return false;
    }

    return true;
  };

  const isPageAllowed = (page: string, currentUsedGlp?: string) => {
    const currentState = useStore.getState();
    if (page === 'used-glp') return true;
    return isPageVisible(page, currentUsedGlp) && !currentState.skippedPages.includes(page);
  };

  const getPreviousPage = () => {
    const currentState = useStore.getState();
    const currentVisitedPages = currentState.visitedPages;
    const currentPageValue = currentState.currentPage;

    if (currentVisitedPages.length === 0) {
      return null;
    }

    const currentPageIndex = currentVisitedPages.indexOf(currentPageValue);

    if (currentPageIndex === -1) {
      return currentVisitedPages.length > 0
        ? currentVisitedPages[currentVisitedPages.length - 1]
        : null;
    }

    if (currentPageIndex > 0) {
      return currentVisitedPages[currentPageIndex - 1];
    }

    return null;
  };

  const onPrev = () => {
    const prevPage = getPreviousPage();
    if (prevPage) {
      setPage(prevPage);
    }
  };

  const onNext = () => {
    const nextPage = getNextPageWithRules();

    if (nextPage) {
      const currentPageValue = useStore.getState().currentPage;
      addVisitedPage(currentPageValue);
      setPage(nextPage);
    }
  };

  return { onPrev, onNext, currentIndex };
}